import * as React from 'react'
import { DataGrid, GridCellProps } from '@mui/x-data-grid'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash/isEmpty'
import { useTheme } from '@mui/material/styles'
import { grey } from '@mui/material/colors'

import { AdminUITheme } from 'types/AdminUiThemeMui'
import ParticipantColumns from 'components/Participants/ParticipantColumns'
import { ParticipantComponentProps } from 'types/Participants'
import LoadingOverlay from 'components/Shared/Grid/LoadingOverlay'
import NoRowsOverlay from 'components/Shared/Grid/NoRows'
import Search from 'components/Shared/Search'
import { ParticpantsContext } from 'containers/Participants'
import {
  setPerPage,
  setPage,
  setSort,
} from 'containers/Participants/Redux/Actions/ParticipantGrid'
import { getRowPerPageOptions, objectDifference } from 'lib/participants/Helper'
import { ASC } from 'lib/GlobalConstants'
import BulkTag from 'components/Participants/BulkTag'
import { useAppDispatch } from 'redux/Hook'
import FilterBuilder from 'components/Participants/FilterBuilder'
import { resetQuestions as resetSignUpFormQuestions } from 'redux/slices/SignUpQuestions'
import AddParticipants from 'components/Participants/AddParticipants'
import SelectAll from 'components/Participants/SelectAll'
import {
  createSelectionModel,
  getFooterRowsSelected,
  handelSelection,
  getIntersectionOfCachedNSelectedParticipants,
  createAvailableParticipant,
} from 'components/Participants/SelectAllHelper'
import { setActiveGroup } from 'redux/slices/ParticipantGroups'
import CommonHeader from 'components/Shared/CommonHeader'
import TableCellWithToolTip from 'components/Shared/Table/TableCellWithToolTip'
import AlleySpacer from 'components/Shared/AlleySpacer'

const Participant = ({ dispatch }: ParticipantComponentProps) => {
  const { t } = useTranslation()
  const dispatchStore = useAppDispatch()

  const {
    state: {
      queries,
      participants,
      loading,
      meta,
      sortModel,
      selectAll,
      selectedParticipantIDs,
    },
    functions: { onSortModelChange, onSelection, setSelectAllObj },
  } = React.useContext(ParticpantsContext)

  React.useEffect(() => {
    document.title = `${t('common.admin')} | ${t('participant.heading')}`
  }, [])

  const theme: AdminUITheme = useTheme()

  const onClearFilter = () => {
    dispatchStore(setActiveGroup(null))
    dispatchStore(resetSignUpFormQuestions())

    dispatch(
      setSort({ login: 'desc' }, [
        {
          field: 'login',
          sort: 'desc',
        },
      ]),
    )
  }

  React.useEffect(() => {
    // setTimout is needed to ensure intercom scripts are loaded and then apply the styles
    const intercomTimeout = setTimeout(() => {
      const intercomButton = document.querySelector(
        '.intercom-launcher',
      ) as HTMLElement

      if (intercomButton) intercomButton.style.bottom = '5rem'
    }, 1000)

    return function cleanup() {
      const intercomButton = document.querySelector(
        '.intercom-launcher',
      ) as HTMLElement

      if (intercomButton) intercomButton.style.bottom = '2.5rem'
      clearTimeout(intercomTimeout)
    }
  }, [])

  const availableParticipants = createAvailableParticipant(participants)
  const countOfSelPageLvlParticipants =
    getIntersectionOfCachedNSelectedParticipants(
      selectedParticipantIDs,
      availableParticipants,
    ).length
  const isAllVisibleSelected =
    (countOfSelPageLvlParticipants === availableParticipants.length &&
      availableParticipants.length !== 0) ||
    false

  const getLocaleText = () => {
    return {
      footerRowSelected: () => {
        const count = getFooterRowsSelected(
          selectAll,
          queries,
          meta,
          selectedParticipantIDs,
        )
        return count == 1
          ? `${t('participant.selectAll.rowSelected', {
              count: count,
            })}`
          : `${t('participant.selectAll.rowsSelected', {
              count: count,
            })}`
      },
      columnHeaderSortIconLabel: t('common.sort'),
    }
  }

  return (
    <>
      <Box sx={theme.Participant.header}>
        <CommonHeader
          isBadgeActive={false}
          title={t('participant.heading')}
          subTitle={t('participant.subHeading')}
          ActionButtons={[AddParticipants]}
          breadcrumb={[
            {
              icon: 'home',
              iconStyle: 'filled',
              link: '/adm',
            },
          ]}
        />
      </Box>
      <AlleySpacer direction={'top'} spacing={2}>
        <Grid container rowSpacing={3}>
          <FilterBuilder onClearFilter={onClearFilter} />

          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={0} sm={5} md={6}>
                <BulkTag />
              </Grid>
              <Grid item xs={12} sm={7} md={6}>
                <Search />
              </Grid>
            </Grid>
          </Grid>
          <AlleySpacer direction={'top'} spacing={3}>
            {(selectAll.all || isAllVisibleSelected) && (
              <Grid item xs={12}>
                <Grid
                  container
                  sx={{ height: '2.5rem', backgroundColor: grey[100] }}
                  alignItems="center"
                  justifyContent="center"
                  style={{ textAlign: 'center' }}
                >
                  <SelectAll
                    totalCount={meta.count}
                    pageLvlCount={countOfSelPageLvlParticipants}
                    setSelectAllObj={setSelectAllObj}
                    selectAllVisibleFlag={isAllVisibleSelected}
                    selectedAllFlag={selectAll?.all || false}
                    onSelection={onSelection}
                  />
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box style={theme.ParticipantGrid.participantGridMesurements}>
                <DataGrid
                  sx={{
                    fontSize: '.875rem',
                    '& a': {
                      fontSize: '.875rem !important',
                    },
                    '& .MuiDataGrid-iconSeparator': {
                      display: 'none',
                    },
                  }}
                  selectionModel={createSelectionModel(
                    selectAll,
                    participants,
                    queries,
                    selectedParticipantIDs,
                  )}
                  rows={participants}
                  onSelectionModelChange={(participantIDs) => {
                    handelSelection(
                      participants,
                      participantIDs,
                      queries,
                      setSelectAllObj,
                      onSelection,
                      selectedParticipantIDs,
                      selectAll,
                    )
                  }}
                  columns={ParticipantColumns().map((column: any) => ({
                    ...column,
                    headerName: t(`participant.grid.${column.headerName}`),
                    headerAlign: 'left',
                  }))}
                  components={{
                    NoRowsOverlay,
                    LoadingOverlay,
                    Cell: (props: GridCellProps) => (
                      <TableCellWithToolTip
                        showTabelCellWithTooltip={true}
                        {...props}
                      />
                    ),
                  }}
                  sortingOrder={['desc', ASC]}
                  disableColumnMenu
                  disableSelectionOnClick
                  checkboxSelection
                  loading={loading}
                  pageSize={queries.per_page}
                  rowsPerPageOptions={getRowPerPageOptions(meta)}
                  paginationMode="server"
                  sortingMode="server"
                  page={queries.page - 1}
                  rowCount={meta.count}
                  onPageSizeChange={(newPageSize: number) =>
                    dispatch(setPerPage(newPageSize))
                  }
                  onPageChange={(newPage: number) =>
                    dispatch(setPage(newPage + 1))
                  }
                  sortModel={sortModel}
                  onSortModelChange={onSortModelChange}
                  componentsProps={{
                    pagination: {
                      getItemAriaLabel: (type: string) =>
                        t(`participant.grid.${type}Page`),
                      role: 'participants-table-pagination',
                      labelRowsPerPage: t('participant.grid.rowsPerPage'),
                      labelDisplayedRows: ({
                        from,
                        to,
                        count,
                      }: {
                        from: string
                        to: string
                        count: string
                      }) =>
                        `${from}–${to} ${t('participant.grid.of')} ${count}`,
                      sx: { marginRight: { xs: '3rem', sm: '2rem' } },
                    },
                  }}
                  localeText={getLocaleText()}
                />
              </Box>
            </Grid>
          </AlleySpacer>
        </Grid>
      </AlleySpacer>
    </>
  )
}

export default React.memo(Participant, (prevProps, nextProps) =>
  isEmpty(objectDifference(nextProps?.state, prevProps?.state)),
)
